import React from 'react';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';

import TrainerCreator from './TrainerCreator.js';
import TrainerDetails from './TrainerDetails.js';

import {createClientUpdate} from '../client/Client.js';
import * as validation from '../util/Forms.js';
import {makeCountrySelection} from '../util/Forms.js';
import {Item} from "../util/UtilComponents";



/*
Root element for rendering a Trainer. Depending on what action is being taken (view, update, create) a sub-element is
rendered.
Also includes a lot of util functions like labelers and schema information for parsing updates.
 */
const Trainer = ({
	// data
	trainer,

	// state
	action,

	// action changes
	startEditing,
	cancelEditing,
	cancelCreating,

	// execution
	create,
	update,
	remove,

	// admin rights
	isAdmin,
	licenseData,
	setTrainerPassword,
}) => {
	let removeButton;
	if (remove) {
		const color = trainer.enabled ? 'btn-danger' : 'btn-warning';
		const icon = trainer.enabled ? 'bi-x-lg' : 'bi-check-lg';
		removeButton = (
			<Button className = {`${color} ml-1`}
				onClick = {() => remove(trainer.id, !trainer.enabled)}>
				<i className = {icon}/>
			</Button>
		);
	}
	const header = (
		<>
			<Col>
				<h3 className = 'kairos-trainer-header'>
					{action === 'create' ? 'Neuer Trainer' : `${trainer.surname}, ${trainer.name}`}
				</h3>
			</Col>
			{!action &&
			<Col xs='auto'>
				{startEditing &&
				<Button className = {'mr-1'}
					onClick = {startEditing}>
					<i className = {'bi-pencil'}/>
				</Button>}
				{remove &&
				removeButton}
			</Col>}
		</>
	);

	let content;

	// key because the Creator isn't dismounted when switching between edit and create

	switch (action) {
		case 'edit': {
			content = (
				<TrainerCreator
					key = {'edit'}
					trainer = {trainer}
					execute = {updatedTrainer => update(createTrainerUpdate(licenseData, trainer, updatedTrainer))}
					cancel = {cancelEditing}
					isAdmin = {isAdmin}
					licenseData = {licenseData}
					setTrainerPassword = {password => setTrainerPassword(password)}/>
			);
			break;
		};
		case 'create': {
			content = (
				<TrainerCreator
					key = {'create'}
					trainer = {trainer}
					execute = {createdTrainer => create(normalizeTrainer(licenseData, createdTrainer))}
					cancel = {cancelCreating}
					licenseData = {licenseData}
					isAdmin = {isAdmin}/>
			);
			break;
		};
		default: {
			content = (
				<TrainerDetails
					trainer = {trainer}
					isAdmin = {isAdmin}/>
			);
			break;
		};
	};



	// render

	return (
		<>
			<Row>
				{header}
			</Row>
			<Row>
				<Col>
					{content}
				</Col>
			</Row>
		</>
	);
};

export default Trainer;

// trainer skeleton for creating a new trainer
export const createSkeletonTrainer = licenseData => ({
	country: 'Deutschland',
	clients: [],
	license: licenseData.find(l => l.license === 'Basis'),
});

const normalizeTrainer = (licenseData, trainer) => ({
	...trainer,
	license: trainer.license.license,
});

const createTrainerUpdate = (licenseData, trainer, edited) => {
	const update = { };

	// country requires special UI but handling is the same
	fields
		.map(f => f.fieldname)
		.concat(['country'])
		.forEach(f => {
			if (edited[f] !== trainer[f])
				update[f] = edited[f];
		});


	// license key
	if (edited.license.license !== trainer.license.license)
		update.license = edited.license.license;


	// clients

	// added: without ID
	update.addedClients = edited.clients.filter(c => !c.id);

	// removed: IDs don't appear in edited
	update.removedClients = trainer.clients
			.map(c => c.id)
			.filter(c => !edited.clients.some(ec => ec.id === c));

	// TODO this shouldn't send empty updates for unchanged clients
	// updated: compare Clients
	update.updatedClients = { };
	edited.clients.forEach(ec => {
		/*
			original client finden
			createClientUpdate für beide
			falls
		*/
		const originalClient = trainer.clients.find(c => c.id === ec.id);
		const clientUpdate = createClientUpdate(originalClient, ec);
		update.updatedClients[ec.id] = clientUpdate;
	});

	return update;
};

export const labeler = t => `${t.surname}, ${t.name}`;

export const fields = [
	{
		label: 'Vorname',
		fieldname: 'name',
		required: true,
		pattern: validation.name.pattern,
		validationMessage: validation.name.message,
	},
	{
		label: 'Nachname',
		fieldname: 'surname',
		required: true,
		pattern: validation.surname.pattern,
		validationMessage: validation.surname.message,
	},
	{
		label: 'Straße',
		fieldname: 'street',
		required: true,
		pattern: validation.street.pattern,
		validationMessage: validation.street.message,
	},
	{
		label: 'Hausnummer',
		fieldname: 'houseNumber',
		required: true,
		pattern: validation.houseNumber.pattern,
		validationMessage: validation.houseNumber.message,
	},
	{
		label: 'PLZ',
		fieldname: 'zipcode',
		required: true,
		pattern: validation.zipCode.pattern,
		validationMessage: validation.zipCode.message,
	},
	{
		label: 'Stadt',
		fieldname: 'city',
		required: true,
		pattern: validation.city.pattern,
		validationMessage: validation.city.message,
	},
	{
		label: 'Telefon',
		fieldname: 'phoneNumber',
		required: true,
		pattern: validation.phoneNumber.pattern,
		validationMessage: validation.phoneNumber.message,
	},
	{
		label: 'E-Mail-Adresse',
		fieldname: 'emailAddress',
		required: true,
		pattern: validation.emailAddress.pattern,
		validationMessage: validation.emailAddress.message,
	},
	makeCountrySelection(true),
];

export const licenseField = {
	label: 'Lizenz',
	fieldname: 'license',
	required: true,
};

export const toListGroupCompact = (trainer, asAdmin) => {
	let entries = [
		(<Item
			key = {'name'}
			label = {'Vorname'}
			value = {trainer.name}/>),
		(<Item
			key = {'surname'}
			label = {'Nachname'}
			value = {trainer.surname}/>),
		(<Item
			key = {'address'}
			label = {'Adresse'}
			value = {`${trainer.street} ${trainer.houseNumber}` +
				`\n${trainer.zipcode} ${trainer.city}` +
				`\n${trainer.country}`}
			valueClasses={'multiline-text'}/>),
		(<Item
			key = {'phone'}
			label = {'Telefon'}
			value = {trainer.phoneNumber}/>),
		(<Item
			key = {'email'}
			label = {'E-Mail-Adresse'}
			value = {
				<a href={`mailto://${trainer.emailAddress}`}>
					{`${trainer.emailAddress}`}
				</a>}/>),
	];

	// admin can see the license
	if (asAdmin)
		entries.push(
			(<Item
				key = {licenseField.fieldname}
				label = {licenseField.label}
				value = {trainer.license.displayName}/>)
		);

	return entries;
};
